/**
 * client/render/codxform.ts — placing CoD-space entities in the three scene. OWNER: RENDER agent.
 *
 * Model GLBs (bodies, weapons, missiles) have a `cod_root` node that maps CoD-local axes into three-local
 * axes (research/models-anims.md §1): three-local +X = CoD forward, +Y = up, -Z = CoD left. An object
 * oriented by CoD angles therefore needs the three-space basis (forward, up, right) as its X/Y/Z columns.
 * We build it from shared/math angleVectors (the engine's AngleVectors) to avoid Euler sign traps.
 */
import * as THREE from 'three';
import { angleVectors, codToThree, type Vec3 } from '@shared/math';

const f: Vec3 = [0, 0, 0], r: Vec3 = [0, 0, 0], u: Vec3 = [0, 0, 0];
const tf: Vec3 = [0, 0, 0], tr: Vec3 = [0, 0, 0], tu: Vec3 = [0, 0, 0], tp: Vec3 = [0, 0, 0];
const m = new THREE.Matrix4();

/** Set obj.position/quaternion from a CoD origin + CoD angles [pitch, yaw, roll] (degrees). */
export function placeCod(obj: THREE.Object3D, origin: ArrayLike<number>, angles: ArrayLike<number>): void {
  angleVectors(angles as Vec3, f, r, u);
  codToThree(tf, f); codToThree(tr, r); codToThree(tu, u);
  m.set(
    tf[0], tu[0], tr[0], 0,
    tf[1], tu[1], tr[1], 0,
    tf[2], tu[2], tr[2], 0,
    0, 0, 0, 1,
  );
  obj.quaternion.setFromRotationMatrix(m);
  codToThree(tp, origin as Vec3);
  obj.position.set(tp[0], tp[1], tp[2]);
}

/** Yaw-only placement (player bodies stand upright). */
export function placeCodYaw(obj: THREE.Object3D, origin: ArrayLike<number>, yawDeg: number): void {
  codToThree(tp, origin as Vec3);
  obj.position.set(tp[0], tp[1], tp[2]);
  obj.quaternion.setFromAxisAngle(THREE.Object3D.DEFAULT_UP, (yawDeg * Math.PI) / 180);
}

/** CoD vector -> THREE.Vector3 (three space). */
export function codVec(out: THREE.Vector3, v: ArrayLike<number>): THREE.Vector3 {
  return out.set(v[0], v[2], -v[1]);
}
